import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import {
  ClientPaymentRequest,
  ClientPaymentResponse,
  InvoiceRequest,
} from '../../../interfaces/gestioncobros.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { AuthService } from '../../../services/auth.service';
import { GestionCobrosService } from '../../../services/gestioncobros.service';
import { ClientPaymentFilter } from '../../../interfaces/filters.interfaces';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar los recibos de cobro
 * Permite visualizar, filtrar y exportar información sobre los recibos de cobro
 */
@Component({
  selector: 'app-recibos',
  standalone: true,
  imports: [
    CommonModule,
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
  ],
  templateUrl: './recibos.component.html',
  styleUrl: './recibos.component.css',
})
export class RecibosComponent {
  insuranceData: ClientPaymentResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  fecha1?: Date;
  fecha2?: Date;
  //Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      { key: 'policyNumber', label: 'Póliza', sortable: true, type: 'poliza' },
      { key: 'debtorCode', label: 'Código', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      { key: 'transactionType', label: 'Tipo', sortable: true },
      { key: 'documentNumber', label: 'Número', sortable: true },
      {
        key: 'effectiveDate',
        label: 'Efectividad',
        sortable: true,
        type: 'date',
      },
      { key: 'workDate', label: 'Fecha de trabajo', sortable: true },
      { key: 'premium', label: 'Prima', sortable: true, type: 'currency' },
      { key: 'tax', label: 'Impuesto', sortable: true, type: 'currency' },
      {
        key: 'invoice',
        label: 'Recibo',
        type: 'invoice',
      },
    ],
    showTotals: true,
    totalFields: ['premium'],
  };
  tableConfigExport: TableConfig = {
    columns: [
      { key: 'policyNumber', label: 'Póliza', sortable: true, type: 'poliza' },
      { key: 'debtorCode', label: 'Código', sortable: true },
      { key: 'clientName', label: 'Asegurado', sortable: true },
      { key: 'transactionType', label: 'Tipo', sortable: true },
      { key: 'documentNumber', label: 'Número', sortable: true },
      {
        key: 'effectiveDate',
        label: 'Efectividad',
        sortable: true,
        type: 'date',
      },
      { key: 'workDate', label: 'Fecha de trabajo', sortable: true },
      { key: 'premium', label: 'Prima', sortable: true, type: 'currency' },
      { key: 'tax', label: 'Impuesto', sortable: true, type: 'currency' },
    ],
    showTotals: true,
    totalFields: ['premium'],
  };

  opcionesSelectFilter = [
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  title: string = '';
  invoice: string = '';

  /**
   * Constructor del componente
   * @param gestionCobrosService Servicio para gestionar los cobros
   * @param authService Servicio de autenticación
   */
  constructor(
    private gestionCobrosService: GestionCobrosService,
    private authService: AuthService,
    private gAnalytics: GoogleAnalyticsService
  ) {
    this.title = 'Mis Recibos de cobro';
  }

  /**
   * Carga los datos iniciales de los recibos de cobro
   * Realiza una consulta al servidor con la fecha actual
   */
  loadInitialData(): void {
    const request: ClientPaymentRequest = {
      ProducerNumber: this.authService.getProducerNumber() || 0,
      DateTo: new Date(),
      DateFrom: new Date(),
    };

    this.gestionCobrosService.getClientPayments(request).subscribe({
      next: (response: HttpResponse<ApiResponse<ClientPaymentResponse[]>>) => {
        if (response.status === 200 && response.body?.data) {
          this.insuranceData = response.body.data;
        }

        if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener las pólizas:', error);
        this.insuranceData = [];
        this.mensajeNoData =
          'No se han podido obtener los datos de los recibos';
      },
    });
  }

  /**
   * Maneja los cambios en los filtros de búsqueda
   * Realiza una nueva consulta al servidor con los filtros actualizados
   * @param filter Objeto con los filtros seleccionados por el usuario
   */
  onFilterChange(filter: ClientPaymentFilter): void {
    if (Object.keys(filter).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }
    this.fecha1 = filter.dateFrom;
    this.fecha2 = filter.dateTo;

    const request: ClientPaymentRequest = {
      ProducerNumber: this.authService.getProducerNumber() || 0,
      DateTo: filter.dateTo || new Date(),
      DateFrom: filter.dateFrom || new Date(),
      ...(filter?.clientCode && { ClientCode: filter.clientCode }),
    };

    // Agregar campos opcionales solo si tienen valor
    if (filter.lob) request.Lob = filter.lob;
    if (filter.slob) request.Slob = filter.slob;
    if (filter.clientCode) request.ClientCode = filter.clientCode;

    this.gestionCobrosService.getClientPayments(request).subscribe({
      next: (response: HttpResponse<ApiResponse<ClientPaymentResponse[]>>) => {
        if (response.status === 200 && response.body?.data) {
          this.insuranceData = response.body.data.map((item) => ({
            ...item,
            invoice: 'No disponible',
          }));
          this.mensajeNoData =
            response.body.data.length === 0
              ? 'No hay datos disponibles para mostrar'
              : '';
        }

        if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.insuranceData = [];
        if (error.status === 404) {
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        } else {
          this.mensajeNoData = 'Ocurrió un error al obtener los datos';
        }
      },
    });
  }

  /**
   * Maneja la exportación de datos en diferentes formatos
   * @param formato Formato de exportación deseado
   */
  onExportar(formato: string): void {
    //Evento para trackear las exportaciones de archivos en Google Analytics
    this.gAnalytics.trackEvent('recibos_exportar',{
      page:'/recibos',
      format:formato
    })
    if (this.tablaComponent) {
      const headerInfo = {
        titulo: 'Recibos de cobros',
        lob: this.getLobName(),
        slob: this.getSlobName(),
        fecha1: this.fecha1!,
        fecha2: this.fecha2,
        tableConfig: this.tableConfig,
      };
      this.tablaComponent.exportarDatos(formato, headerInfo);
    }
  }

  // Extrayendo los nombres de los ramos y subramos
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo (LOB) seleccionado
   * @returns Nombre del ramo o cadena vacía si no hay selección
   */
  public getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo (SLOB) seleccionado
   * @returns Nombre del subramo o cadena vacía si no hay selección
   */
  public getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }

  /**
   * Maneja la impresión de un recibo específico
   * @param item Recibo a imprimir
   */
  onPrintInvoice(item: any) {
    const request: InvoiceRequest = {
      documentNumber: item.documentNumber,
      branchCode: item.branchCode,
      officeCode: item.officeCode,
      producerNumber: this.authService.getProducerNumber() || 0,
      producerName: this.authService.getProducerName() || '',
    };

    this.gestionCobrosService.getClientPaymentsInvoice(request).subscribe({
      next: (response: ApiResponse<any>) => {
        window.open(response.data.url, '_blank');
      },
      error: (error) => {
        console.error('Error al obtener el recibo:', error);
        window.alert(
          'Error al obtener el recibo. Por favor, intente nuevamente.'
        );
      },
    });
  }
}
